import { AppState } from '../app.state';
import { createSelector } from '@ngrx/store';
import { rainSystemDefaultState } from './steps/measurements/rain-system/rain-system.constants';
import { MeasurementsType } from './steps/measurements/measurements.constants';
import { CalculationDto } from '../../api/models/calculation-dto';
import { RainSystemMeasurementsDto } from '../../api/models/rain-system-measurements-dto';
import { AnnexDto } from '../../api/models/annex-dto';
import { AccessoryDto } from '../../api/models/accessory-dto';
import { CalculationTypeEnum } from '../../api/models/calculation-type-enum';
import { OneSlopeMeasurementDto } from '../../api/models/one-slope-measurement-dto';
import { TwoSlopeMeasurementDto } from '../../api/models/two-slope-measurement-dto';
import { FourSlopeMeasurementDto } from '../../api/models/four-slope-measurement-dto';
import { CustomMeasurementDto } from '../../api/models/custom-measurement-dto';
import { CustomShapesMeasurementDto } from '../../api/models/custom-shapes-measurement-dto';
import { ContactDto } from '../../api/models/contact-dto';
import { ShoppingCartDto } from '../../api/models/shopping-cart-dto';
import { RoofProducerEnum } from 'src/api/models/roof-producer-enum';
import { RoofProductDto } from '../../api/models/roof-product-dto';
import { RoofThicknessDto } from '../../api/models/roof-thickness-dto';
import { RoofColorDto } from '../../api/models/roof-color-dto';
import { RoofFinishDto } from '../../api/models/roof-finish-dto';
import { FenceSlatMeasurementsDto } from '../../api/models/fence-slat-measurements-dto';

export interface Calculations {
  activeTab: CalculationTypeEnum;
  partialPayment: boolean;
  stepperStep: number;
  calculatePrice: boolean;
  models?: CalculationModelsState;
  contact: ContactDto;
  isCompany: boolean;
  cart?: ShoppingCartDto;
  [CalculationTypeEnum.OneSlope]: CalculationState<OneSlopeMeasurementDto>;
  [CalculationTypeEnum.TwoSlopes]: CalculationState<TwoSlopeMeasurementDto>;
  [CalculationTypeEnum.FourSlopes]: CalculationState<FourSlopeMeasurementDto>;
  [CalculationTypeEnum.Custom]: CalculationState<CustomMeasurementDto>;
  [CalculationTypeEnum.CustomShape]: CalculationState<CustomShapesMeasurementDto>;
  [CalculationTypeEnum.FenceSlat]: CalculationState<FenceSlatMeasurementsDto>;
  [CalculationTypeEnum.FencePanel]: CalculationState<any>;
}

export interface CalculationModelsState {
  producer: RoofProducerEnum;
  roof: RoofProductDto;
  color: RoofColorDto;
  finish: RoofFinishDto;
  thickness: RoofThicknessDto;
  hasDripStop: boolean;
}

export interface CalculationState<MeasurementsInterface = MeasurementsType> {
  isLoaded: boolean;
  partialPayment: boolean;
  calculateAccessories: boolean;
  id?: string;
  accessories: AccessoryDto[];
  measurements: MeasurementsInterface;
  rainSystem: RainSystemMeasurementsDto;
  annexes: AnnexDto[];
  roofType: CalculationTypeEnum;
  apiCalculation?: CalculationDto;
}

export const defaultCalculationState = {
  isLoaded: false,
  partialPayment: false,
  calculateAccessories: false,
  calculatePrice: false,
  measurements: {
    chimneys: 1,
    splitHeightInTwoRows: false,
  },
  rainSystem: rainSystemDefaultState,
  accessories: [],
  annexes: [],
};

export const defaultCustomCalculationState: CalculationState<CustomMeasurementDto> = {
  roofType: CalculationTypeEnum.Custom,
  isLoaded: false,
  partialPayment: false,
  calculateAccessories: false,
  rainSystem: rainSystemDefaultState,
  measurements: {
    tilesByHeight: [{ height: null, count: null } as any],
  },
  accessories: [],
  annexes: [],
};

export const defaultCustomShapeCalculationState: CalculationState<CustomShapesMeasurementDto | any> = {
  roofType: CalculationTypeEnum.CustomShape,
  isLoaded: false,
  partialPayment: false,
  calculateAccessories: false,
  rainSystem: rainSystemDefaultState,
  measurements: {
    shapes: [],
  },
  accessories: [],
  annexes: [],
};

export const oneSlopeMeasurementsState: OneSlopeMeasurementDto | any = {
  width: null,
  height: null,
  chimneys: 1,
  splitHeightInTwoRows: false,
};
export const twoSlopeMeasurementsState: TwoSlopeMeasurementDto = {
  ...oneSlopeMeasurementsState,
  height2: null,
};
export const fourSlopeMeasurementsState: FourSlopeMeasurementDto | any = {
  triangleHeight: null,
  triangleBase: null,
  trapezoidHeight: null,
  trapezoidBase: null,
  ridgeLength: null,
  splitHeightInTwoRows: false,
  chimneys: 1,
};

type NullableValues<T> = {
  [P in keyof T]: T[P] | null;
};

export const fenceSlatsMeasurementsState: NullableValues<FenceSlatMeasurementsDto> = {
  width: null,
  height: null,
  distanceBetweenSlats: 0.1,
};
export const calculationInitialState: Calculations = {
  activeTab: CalculationTypeEnum.OneSlope,
  partialPayment: false,
  stepperStep: 1,
  calculatePrice: false,
  isCompany: false,
  models: undefined,
  contact: {} as any,
  cart: undefined,
  [CalculationTypeEnum.OneSlope]: {
    ...defaultCalculationState,
    measurements: oneSlopeMeasurementsState,
    roofType: CalculationTypeEnum.OneSlope,
  },
  [CalculationTypeEnum.TwoSlopes]: {
    ...defaultCalculationState,
    measurements: twoSlopeMeasurementsState,
    roofType: CalculationTypeEnum.TwoSlopes,
  },
  [CalculationTypeEnum.FourSlopes]: {
    ...defaultCalculationState,
    measurements: fourSlopeMeasurementsState,
    roofType: CalculationTypeEnum.FourSlopes,
  },
  [CalculationTypeEnum.Custom]: defaultCustomCalculationState,
  [CalculationTypeEnum.CustomShape]: defaultCustomShapeCalculationState,
  [CalculationTypeEnum.FencePanel]: defaultCustomShapeCalculationState,
  [CalculationTypeEnum.FenceSlat]: {
    ...defaultCalculationState,
    roofType: CalculationTypeEnum.FenceSlat,
    measurements: fenceSlatsMeasurementsState as FenceSlatMeasurementsDto,
  },
};
export const selectCalculations = (state: AppState) => state.calculations;
export const selectCalculation = createSelector(selectCalculations, (state: Calculations) => state[state.activeTab]);
export const selectRainSystem = createSelector(selectCalculation, (state) => state.rainSystem);
export const selectModels = createSelector(selectCalculations, (state: Calculations) => state.models);
export const selectStepperStep = createSelector(selectCalculations, (state: Calculations) => state.stepperStep);
